import React, { Fragment, useState } from 'react';
import { useRecordContext } from 'react-admin';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { capitalize } from 'inflection';
import LinkField from 'components/LinkField';
import { formatDateTime } from 'components/DateTimeField';
import { DisplayHelpers } from 'utils/helpers';
import reverse from 'lodash/reverse';
import { RateSettingsDisplayHelper } from 'utils/displayHelpers/rate-settings';
import isNil from 'lodash/isNil';
import get from 'lodash/get';

const FIELD_CONFIGS = [
  {
    key: 'charge_rate_cents',
    label: 'charge_rate_cents',
    path: 'ten99.charge_rate_cents',
    formatter: DisplayHelpers.money,
    source: 'settings',
  },
  {
    key: 'margin_percent_1099',
    label: 'margin_percent_1099',
    path: 'ten99.margin_percent_1099',
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'ten99_blended_rate_markup',
    label: 'ten99.blended_rate_markup',
    customGetter: (record) => {
      const helper = record && new RateSettingsDisplayHelper({ settings: record });
      return helper?.getBlendedRateMarkup('ten99');
    },
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'w2_blended_rate_markup',
    label: 'w2_per_diem.blended_rate_markup',
    customGetter: (record) => {
      const helper = record && new RateSettingsDisplayHelper({ settings: record });
      return helper?.getBlendedRateMarkup('w2.local');
    },
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'ten99_use_blended_rate',
    label: 'ten99.use_blended_rate',
    path: 'ten99.use_blended_rate',
    formatter: (value) => (isNil(value) ? null : String(value)),
    source: 'settings',
  },
  {
    key: 'w2_use_blended_rate',
    label: 'w2.local.use_blended_rate',
    path: 'w2.local.use_blended_rate',
    formatter: (value) => (isNil(value) ? null : String(value)),
    source: 'settings',
  },
  {
    key: 'margin_percent_1099_guardrail',
    label: 'margin_percent_1099_guardrail',
    formatter: DisplayHelpers.percentage,
    source: 'column',
  },
  {
    key: 'margin_percent_w2_guardrail',
    label: 'margin_percent_w2_guardrail',
    formatter: DisplayHelpers.percentage,
    source: 'column',
  },
  {
    key: 'payout_rate_percent_w2_guardrail',
    label: 'payout_rate_percent_w2_guardrail',
    formatter: DisplayHelpers.percentage,
    source: 'column',
  },
  {
    key: 'payout_rate_percent_1099_guardrail',
    label: 'payout_rate_percent_1099_guardrail',
    formatter: DisplayHelpers.percentage,
    source: 'column',
  },
  {
    key: 'incremental_margin_percent_1099',
    label: 'incremental_margin_percent_1099',
    formatter: DisplayHelpers.percentage,
    source: 'column',
  },
  {
    key: 'incremental_margin_percent_w2',
    label: 'incremental_margin_percent_w2',
    formatter: DisplayHelpers.percentage,
    source: 'column',
  },
];

function renderAuditRow(config, audit) {
  const { action, account, audited_changes, created_at, user_id } = audit;
  const isUpdate = action === 'update';
  let originalValue, updatedValue, hasChanged;

  if (config.source === 'settings') {
    const [originalRecord, updatedRecord] = isUpdate
      ? audited_changes.settings
      : [audited_changes.settings];

    if (config.customGetter) {
      originalValue = config.customGetter(originalRecord);
      updatedValue = config.customGetter(updatedRecord);
    } else {
      originalValue = get(originalRecord, config.path);
      updatedValue = get(updatedRecord, config.path);
    }

    hasChanged = !isUpdate || `${updatedValue}` !== `${originalValue}`;
  } else {
    hasChanged = audited_changes[config.key] !== undefined;
    [originalValue, updatedValue] = isUpdate
      ? audited_changes[config.key] || [null, null]
      : [null, audited_changes[config.key]];
  }

  if (!hasChanged) {
    return null;
  }

  const formattedOriginal =
    originalValue !== null && originalValue !== undefined ? config.formatter(originalValue) : null;
  const formattedUpdated =
    updatedValue !== null && updatedValue !== undefined ? config.formatter(updatedValue) : null;

  return (
    <TableRow key={`${audit.id}-${config.key}`}>
      <TableCell>{capitalize(action)}</TableCell>
      <TableCell>
        {formatDateTime({ timeZone: 'America/Los_Angeles' })('MM/dd/yyyy HH:mm zzz')(created_at)}
      </TableCell>
      <TableCell>
        {!!account && (
          <LinkField to={`/Account/${user_id}/show`} href="_blank">
            {account.name}
          </LinkField>
        )}
      </TableCell>
      <TableCell>{config.label}</TableCell>
      <TableCell title={originalValue}>{formattedOriginal}</TableCell>
      <TableCell title={updatedValue}>{formattedUpdated}</TableCell>
    </TableRow>
  );
}

const RateAudits = () => {
  const record = useRecordContext();
  const [open, setOpen] = useState(false);
  return !!record.audits ? (
    <>
      <Button variant="outlined" color="primary" onClick={() => setOpen(true)}>
        View Rates Change Log
      </Button>

      <Dialog fullWidth maxWidth="lg" open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Rates Change Log</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Action</TableCell>
                <TableCell>Updated At</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Field</TableCell>
                <TableCell>Original</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reverse([...record.audits]).map((audit) => (
                <Fragment key={`audit-table-row-${audit.id}`}>
                  {FIELD_CONFIGS.map((config) => renderAuditRow(config, audit))}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  ) : null;
};

export default RateAudits;
